import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import MetaTags from "react-meta-tags";
import SweetAlert from "react-bootstrap-sweetalert";
import NumberFormat from "react-number-format";
import { AvField } from "availity-reactstrap-validation";
import { Card, CardBody, Col, Container, Row, Button, FormGroup, Modal, ModalHeader, ModalBody } from "reactstrap";
import filterFactory from "react-bootstrap-table2-filter";
import cellEditFactory, { Type } from "react-bootstrap-table2-editor";
import { Package, CircleUser, Handshake, Receipt, CheckCircle, AlertCircle, ScrollText } from "lucide-react";
import danger from "../../assets/images/danger.png";
import { showToast } from "../../components/Toast";
import { currentDateForTransfer } from "../../helpers/current_date_helper";
import { transfersEuropeApi } from "../../services/TransfersService";
import PageLoader from "../../components/Loaders/newPageLoader";
import { motion, AnimatePresence } from "framer-motion";
//Import Breadcrumb
import Breadcrumbs from "../../components/Breadcrumb";
import ExcelJS from "exceljs";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import { socket } from "../../app-socket/socket";
import { WsEvents } from "../../app-socket/events";
import { onAddTransfer, onUpdateTransfer, onDeleteTransfer } from "../../app-socket/events/transfer";
import { fetchTrackingStatuses } from "../../api/trackingApi";
import TrackingColumnPrTest from "../../components/TrackingColumnPrTest";
import useLoadingProgress from "../../hooks/useLoadingProgress";
import { MainContent } from "../../components/Table/MainContent";
import { setActiveTransferQueryArgs } from "../../store/transfers/transferSlice";

export default function TransfersList({ profileData }) {
  const dispatch = useDispatch();
  const {
    data: baseData,
    isFetching: isLoadingBase,
    error,
    refetch,
  } = transfersEuropeApi.useFetchLatestTransfersQuery(profileData._id, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
  });

  const [showLogModal, setShowLogModal] = useState(false);
  const [logModalOrderId, setLogModalOrderId] = useState(null);

  const {
    data: logs = [],
    isLoading: logsLoading,
    refetch: refetchLogs,
  } = transfersEuropeApi.useGetLogsByOrderIdQuery(logModalOrderId, {
    skip: !showLogModal || !logModalOrderId,
  });

  const [getTransfers, { data: unfilteredData, isFetching: isLoadingUnfiltered }] = transfersEuropeApi.useLazyFetchLatestTransfersQuery();
  const [getFilteredTransfers, { data: filteredData, isFetching: isLoadingFiltered }] = transfersEuropeApi.useLazyFilterTransfersQuery();

  // ── which view is active? 'base' | 'unfiltered' | 'filtered'
  const [view, setView] = useState("base");

  const [transfers, setTransfersData] = useState([]);
  const [transfer, setTransfer] = useState({ invoiceNo: "" });
  const [filteredTransfers, setFilteredTransfers] = useState(null);
  const [filteredStatusesTransfers, setFilteredStatusesTransfers] = useState([]);
  const [filters, setNoDbFilters] = useState([]);
  const [agents, setAgentsData] = useState([]);
  const [allAgents, setAllAgentsData] = useState([]);
  const [fees, setFeesData] = useState([]);
  const [boxSizes, setBoxSizesData] = useState([]);
  const [managers, setManagersData] = useState([]);

  const [boxSizeW, setBoxSizeW] = useState(0);
  const [dpdSize, setDpdSize] = useState(0);

  const [isChecked, setChecked] = useState(true);
  const [isEdit, setIsEdit] = useState(false);

  const [modalOrder, setOrderModal] = useState(false);
  const [modalPayment, setPaymentModal] = useState(false);

  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const formatDateForInput = (date) => date.toISOString().split("T")[0];
  const defaultValues = {
    startDate: formatDateForInput(new Date(new Date().setDate(new Date().getDate() - 30))),
    endDate: formatDateForInput(new Date()),
    statuses: [],
  };
  const [filterFormValues, setFilterFormValues] = useState(defaultValues);

  const loading = view === "filtered" ? isLoadingFiltered : view === "unfiltered" ? isLoadingUnfiltered : isLoadingBase;

  const metaSource = useMemo(() => {
    return unfilteredData || baseData || null;
  }, [unfilteredData, baseData]);

  const mergedData = useMemo(() => {
    if (view === "filtered") {
      const filteredReqs = filteredData?.transfers ?? [];
      // if we have a full meta source, keep it and just replace transfers
      return metaSource ? { ...metaSource, transfers: filteredReqs } : { transfers: filteredReqs };
    }
    // for 'unfiltered' and 'base', just use the actual datasets
    return view === "unfiltered" ? unfilteredData || baseData : baseData;
  }, [view, filteredData, unfilteredData, baseData, metaSource]);

  useEffect(() => {
    // Join WebSocket room on mount
    socket.emit(WsEvents.transfer.JOIN_ROOM, console.log);
    // Leave room and clear timeouts on unmount
    return () => {
      socket.emit(WsEvents.transfer.LEAVE_ROOM, console.log);
    };
  }, []); // empty dependency array = runs only once on mount/unmount

  const { progress, step, visible } = useLoadingProgress({
    isLoading: loading,
    isFetching: loading,
    isLoadingUnfiltered,
    isLoadingFiltered,
  });

  useEffect(() => {
    if (!mergedData) return;
    const { transfers = [], agents = [], allAgents = [], fees = [], boxSizes = [], managers = [] } = mergedData;
    setAgentsData((prev) => (JSON.stringify(prev) !== JSON.stringify(agents) ? [...agents] : prev));
    setAllAgentsData((prev) => (JSON.stringify(prev) !== JSON.stringify(allAgents) ? [...allAgents] : prev));
    setTransfersData((prev) => (JSON.stringify(prev) !== JSON.stringify(transfers) ? [...transfers] : prev));
    setFeesData((prev) => (JSON.stringify(prev) !== JSON.stringify(fees) ? [...fees] : prev));
    setBoxSizesData((prev) => (JSON.stringify(prev) !== JSON.stringify(boxSizes) ? [...boxSizes] : prev));
    setManagersData((prev) => (JSON.stringify(prev) !== JSON.stringify(managers) ? [...managers] : prev));
  }, [JSON.stringify(mergedData)]);

  useEffect(() => {
    if (showLogModal && logModalOrderId) {
      refetchLogs();
    }
  }, [showLogModal, logModalOrderId]);

  const [cardHeight, setCardHeight] = useState(0);
  const cardRef = useRef(null);
  useEffect(() => {
    if (!cardRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const newHeight = entry.contentRect.height;
        if (newHeight > 0) setCardHeight(newHeight);
      }
    });
    resizeObserver.observe(cardRef.current);
    return () => resizeObserver.disconnect();
  }, []);
  useEffect(() => {
    const applyFilters = () => {
      let result = [...transfers];
      // console.log(filters?.agents)
      // if (filters?.agents?.length > 0) {
      //   result = result.filter((transfer) => filters?.agents.includes(transfer.agent?._id));
      // }
      const agentFilterValues = filters?.agents ? (Array.isArray(filters.agents) ? filters.agents : [filters.agents]) : [];
      if (agentFilterValues.length > 0) {
        // build mapping: mainId -> all ids for that group
        const mainIdToIds = groupedAgents.reduce((acc, group) => {
          acc[group.id] = group.ids;
          return acc;
        }, {});
        // expand selected main ids to all real agent ids
        const selectedIds = agentFilterValues.reduce((all, mainId) => {
          const ids = mainIdToIds[mainId] || [mainId]; // fallback if not in map
          return all.concat(ids);
        }, []);
        result = result.filter((transfer) => selectedIds.includes(transfer.agent?._id));
      }
      setFilteredStatusesTransfers(result);
    };

    applyFilters();
  }, [filters, transfers]);

  const location = useLocation();
  const [invoiceNoFromPriority, setInvoiceNoFromPriority] = useState("");
  useEffect(() => {
    if (location.state) {
      setInvoiceNoFromPriority(location.state); // or location.state.invoiceNo if it's nested
    }
  }, [location.state]);
  useEffect(() => {
    if (invoiceNoFromPriority && transfers.length > 0) {
      callEditOnLoad();
    }
  }, [invoiceNoFromPriority, transfers]);

  const normalizeName = (name = "") => name.toLowerCase().replace(/\s+/g, " ").trim();

  const groupedAgents = Object.values(
    allAgents.reduce((acc, ag) => {
      const key = normalizeName(ag.mainName);

      if (!acc[key]) {
        acc[key] = {
          mainName: ag.mainName, // label to show
          id: ag._id,
          ids: [ag._id], // all ids for this mainName
          hasActive: !ag.onVacation,
        };
      } else {
        acc[key].ids.push(ag._id);
        if (!ag.onVacation) {
          acc[key].hasActive = true;
          acc[key].id = ag._id;
        }
      }

      return acc;
    }, {}),
  );
  /**
   * Iterates through the `transfers` array to find an element whose `invoiceNo`
   * matches the `invoiceNoFromPriority.invoiceNoFromPriority` value.
   * When a match is found, triggers the `handleEditOrderClick` function with
   * specific parameters to initiate editing of the matched order, then exits the loop.
   */
  const callEditOnLoad = () => {
    for (const element of transfers) {
      if (element.invoiceNo === invoiceNoFromPriority.invoiceNoFromPriority) {
        handleEditOrderClick("", "not selected", element);
        break;
      }
    }
  };

  const showAllOrders = async () => {
    setChecked((prev) => {
      const next = !prev;
      const id = next ? profileData?._id : "all"; // decide which id you use for "my" vs "all"
      getTransfers(id); // fire lazy unfiltered list
      setView("unfiltered"); // show the unfiltered lazy result
      return next;
    });
  };
  /**
   * Toggles the visibility state of the order modal.
   * Uses a callback to invert the current modal state.
   *
   * @function
   * @returns {void}
   */
  const toggleOrderModal = useCallback(() => {
    setOrderModal((prev) => !prev);
  }, []);
  /**
   * Toggles the visibility state of the payment modal.
   * Uses a callback to invert the current modal state.
   *
   * @function
   * @returns {void}
   */
  const togglePaymentModal = useCallback(() => {
    setPaymentModal((prev) => !prev);
  }, []);
  /**
   * Handles the logic for initiating a new order when the user clicks the "New Order" button.
   * This function resets relevant state variables, toggles the order modal, fetches a new invoice number,
   * and sets default values for various order-related options and checkboxes.
   *
   * @async
   * @returns {Promise<void>} Resolves when the new invoice number is fetched and state is updated.
   */
  const handleNewOrderClick = async () => {
    toggleOrderModal();
    setIsEdit(false);
  };
  /**
   * Handles the click event for initiating a new payment.
   * Resets the transfer state, disables edit mode, and opens the payment modal.
   *
   * @async
   * @param {Event} e - The click event object.
   * @param {Object} order - The order object associated with the payment.
   */
  const handleNewPaymentClick = async (e, order) => {
    setTransfer("");
    // setErrorMsg('');
    setIsEdit(false);
    togglePaymentModal();
  };
  /**
   * Handles the edit order click event for a transfer item.
   * Depending on the type parameter, this function updates the transfer state,
   * manages agent assignment, and triggers updates to transfer status or agent data.
   *
   * @param {Event} e - The event object from the click or change event.
   * @param {string} type - The type of action to perform (e.g., 'not selected', 'selected', 'back-needed', etc.).
   * @param {Object} transfer - The transfer object being edited.
   */
  const handleEditOrderClick = (e, type, transfer) => {
    setTransfer(transfer);
    setDpdSize(transfer.dpdSize);
    setBoxSizeW(transfer.boxSize === 0 ? 0 : transfer.boxSize === 1 ? 1 : transfer.boxSize === 2 ? 2 : transfer.boxSize === 3 ? 4 : transfer.boxSize === 4 ? 8 : transfer.boxSize === 5 ? 13 : 20);
    setIsEdit(true);
    if (type !== "not selected" && type !== "selected") {
      const typeMappings = {
        "back-needed": { workNeeded: true, currentlyWorking: false },
        "back-working": { currentlyWorking: true, workDone: false },
        "update-status-working": { workNeeded: false, currentlyWorking: true },
        "update-status-completed": { currentlyWorking: false, workDone: true },
      };
      const transferOrderStatus = {
        _id: transfer._id,
        ...typeMappings[type],
      };
      const id = isChecked ? profileData?._id : "all";
      onUpdateTransfer({ dispatch, updatedData: { updateTransfer: transferOrderStatus, id: id } });
    } else if (type === "selected") {
      const updateTransfer = {
        _id: transfer._id,
        agent: e.target.value.split("|")[0],
        managerID: profileData?._id,
      };
      let updateOldAgent = [];
      let updateNewAgent = [];
      agents.map((agent) => {
        if (transfer.agent != null && transfer.agent._id === agent._id) {
          updateOldAgent = {
            _id: transfer.agent._id,
            mainName: transfer.agent.mainName,
            orderAmount: agent.orderAmount - 1,
            paymentAmount: agent.paymentAmount - parseInt(e.target.value.split("|")[1]),
          };
          return updateOldAgent;
        } else if (e.target.value.split("|")[0] === agent._id) {
          updateNewAgent = {
            _id: e.target.value.split("|")[0],
            mainName: agent.mainName,
            orderAmount: agent.orderAmount + 1,
            paymentAmount: agent.paymentAmount + parseInt(e.target.value.split("|")[1]),
          };
          return updateNewAgent;
        }
      });
      const id = isChecked ? profileData?._id : "all";
      onUpdateTransfer({ dispatch, updatedData: { updateTransfer: updateTransfer, updateOldAgent: updateOldAgent, updateNewAgent: updateNewAgent, id: id } });
    } else {
      if (transfer.agentPayment) {
        togglePaymentModal();
      } else {
        toggleOrderModal();
      }
    }
  };
  /**
   * Handles the action of copying an order by setting the current transfer details,
   * updating the DPD size, mapping the box size to its corresponding value,
   * setting the edit mode to false, and toggling the order modal.
   *
   * @param {Object} transfer - The transfer object containing order details to be copied.
   */
  const handleCopyOrderClick = (transfer) => {
    setTransfer(transfer);
    setDpdSize(transfer.dpdSize);
    setBoxSizeW(transfer.boxSize === 0 ? 0 : transfer.boxSize === 1 ? 1 : transfer.boxSize === 2 ? 2 : transfer.boxSize === 3 ? 4 : transfer.boxSize === 4 ? 8 : transfer.boxSize === 5 ? 13 : 20);
    setIsEdit(false);
    toggleOrderModal();
  };
  /**
   * Handles the submission of a new or edited transfer order.
   *
   * This function processes form values to calculate agent fees, assign agents, and prepare transfer data.
   * Depending on whether the operation is an edit or a new order, it updates or creates a transfer entry,
   * updates agent payment and order counts, and triggers the appropriate Redux actions.
   *
   * @async
   * @function handleNewOrderSubmit
   * @param {Event} e - The form submission event.
   * @param {Object} values - The form values containing transfer details.
   * @returns {Promise<void>}
   */
  const handleNewOrderSubmit = async (e, values) => {
    let weight = values["weight"] !== "" ? values["weight"] : 0;
    let boxSize = values["boxSize"].split("/")[0];
    let boxSizeW = values["boxSize"] !== "" ? values["boxSize"].split("/")[1] : 0;
    let agentFee = 0;
    if (parseInt(boxSizeW) <= weight) {
      boxSizeW = weight;
    }
    for (const element of fees) {
      if (boxSizeW === 0) {
        agentFee = 0;
        break;
      } else if (parseInt(boxSizeW) <= parseInt(element.weightKG)) {
        agentFee = element.agentFee;
        break;
      } else if (parseInt(boxSizeW) > 13) {
        agentFee = 95;
        break;
      }
    }
    if (isEdit) {
      let agent = [];
      let newAgentFee = null;
      agents.map((id) => {
        if (transfer.agent._id === id._id) {
          agent.push(id);
        }
        return agent;
      });
      if (agent.length !== 0) {
        const newFee = agent[0]["paymentAmount"] - transfer.agentPaymentAmount;
        newAgentFee = {
          _id: agent[0]["_id"],
          mainName: agent[0]["mainName"],
          paymentAmount: newFee + agentFee,
          orderAmount: agent[0]["orderAmount"],
        };
      }
      const updateTransfer = {
        _id: transfer._id,
        invoiceNo: values.invoiceNo,
        clientName: values.clientName,
        trackingNo: values.trackingNo,
        managerID: profileData?._id,
        boxSize: boxSize,
        weight: values.weight,
        dpdSize: values.dpdSize,
        clientPayment: values.clientPayment,
        customFees: values.customFees,
        senderType: values.senderType,
        manager: values.manager,
        toxins: values.toxins,
        address: values.address,
        comments: values.comments,
        agentPaymentAmount: agentFee,
        completed: false,
      };
      // updated order
      const id = isChecked ? profileData?._id : "all";
      const updatedData = { updateTransfer: updateTransfer, newAgentFee: newAgentFee, id: id };
      onUpdateTransfer({ dispatch, updatedData });
      setInvoiceNoFromPriority("");
    } else {
      let lowestPayment = [];
      let lowestOrders = [];
      lowestPayment = [...new Map(agents.map((v) => [v.mainName, v])).values()];
      lowestPayment.sort(function (a, b) {
        return a.paymentAmount - b.paymentAmount;
      });
      agents.map((id) => {
        if (lowestPayment[0]["mainName"] === id.mainName && id.onVacation === false) {
          lowestOrders.push(id);
        }
        return lowestOrders;
      });
      lowestOrders.sort(function (a, b) {
        return a.orderAmount - b.orderAmount;
      });
      const newAgentFee = {
        _id: lowestOrders[0]["_id"],
        mainName: lowestOrders[0]["mainName"],
        paymentAmount: lowestOrders[0]["paymentAmount"] + agentFee,
        orderAmount: lowestOrders[0]["orderAmount"] + 1,
      };
      const newTransfer = {
        invoiceNo: values["invoiceNo"],
        clientName: values["clientName"],
        boxSize: boxSize !== "undefined" ? boxSize : 0,
        weight: values["weight"],
        dpdSize: dpdSize,
        clientPayment: values["clientPayment"],
        customFees: values["customFees"],
        senderType: values["senderType"],
        manager: values["manager"],
        toxins: values["toxins"],
        address: values["address"],
        comments: values["comments"],
        agent: lowestOrders[0]["_id"],
        agentPaymentAmount: agentFee,
        agentPaid: false,
        completed: false,
        problem: false,
        workNeeded: false,
        currentlyWorking: false,
        workDone: false,
      };
      setDpdSize(0);
      const id = isChecked ? profileData?._id : "all";
      const combinedData = [newTransfer, newAgentFee, id];
      onAddTransfer({ dispatch, combinedData });
    }
    toggleOrderModal();
  };
  /**
   * Handles the submission of a new or edited agent payment form.
   * Depending on the mode (edit or add), it constructs the appropriate payment object
   * and dispatches the corresponding action to update or add a transfer.
   * After processing, it toggles the payment modal.
   *
   * @async
   * @function handleNewPaymentSubmit
   * @param {Event} e - The form submission event.
   * @param {Object} values - The form values containing agent payment details.
   */
  const handleNewPaymentSubmit = async (e, values) => {
    const id = isChecked ? profileData?._id : "all";
    if (isEdit) {
      const updateAgentPayment = {
        _id: transfer._id,
        managerID: profileData?._id,
        agent: values.agent,
        comments: values.comments,
        agentPaymentAmount: values.agentPaymentAmount,
        agentPaid: values.agentPaid,
      };
      // updated order
      const updatedData = { updateTransfer: updateAgentPayment, id: id };
      onUpdateTransfer({ dispatch, updatedData });
      // onUpdateTransfer({ dispatch, updatedData: { updateTransfer: updateAgentPayment, id: id } });
    } else {
      const newAgentPayment = {
        agent: values["agent"],
        comments: values["comments"],
        agentPaymentAmount: values["agentPaymentAmount"],
        manager: profileData._id,
        agentPaid: false,
        agentPayment: true,
      };
      onAddTransfer({ dispatch, combinedData: [newAgentPayment, [], id] });
    }
    togglePaymentModal();
  };
  /**
   * Handles saving changes to a cell in the transfers table.
   *
   * This function updates the transfer record when a cell value is changed.
   * It prepares the updated transfer object, processes tracking numbers if the changed field is "trackingNo",
   * triggers tracking status fetching, and dispatches the update action.
   *
   * @async
   * @param {*} oldValue - The previous value of the cell.
   * @param {*} newValue - The new value entered in the cell.
   * @param {Object} row - The entire row object representing the transfer.
   * @param {Object} column - The column object containing metadata about the cell.
   * @param {string|number} rowId - The unique identifier for the row.
   * @param {string|number} tabId - The identifier for the current tab.
   * @param {Array} columns - The array of column definitions.
   */
  const handleSaveCell = async (oldValue, newValue, row, column, rowId, columns) => {
    if (oldValue === newValue) return;
    let field = column.dataField;
    const updateTransfer = {
      _id: row._id,
      managerID: profileData?._id,
      bitrixID: row.manager.bitrixID,
      searchInv: row.searchInv,
      [field]: newValue,
      ...(field === "trackingNo" && { delivered: false }), // Add `delivered: false` only when field is "trackingNo"
      sendNotification: field === "trackingNo" && oldValue !== newValue ? true : false,
    };
    if (field === "trackingNo") {
      const trackings =
        typeof newValue === "string"
          ? newValue
              .split(/[, .\n]+/)
              .map((num) => num.trim())
              .filter((num) => /\d/.test(num))
          : Array.isArray(newValue)
            ? newValue
            : [];

      updateTransfer.trackingNo = trackings;
      fetchTrackingStatuses(trackings, "register");
    }

    const id = isChecked ? profileData?._id : "all";
    const updatedData = { updateTransfer: updateTransfer, id: id };
    onUpdateTransfer({ dispatch, updatedData });
    if (showLogModal && logModalOrderId === row._id) {
      refetchLogs();
    }
  };
  const filterFormSubmit = async () => {
    const createOrderBtn = document.getElementById("create-order-btn");
    const myOrdersBtn = document.getElementById("myOrders");
    if (createOrderBtn && myOrdersBtn) {
      const isSameDate = filterFormValues.startDate === formatDateForInput(new Date(new Date().setDate(new Date().getDate() - 30))) && filterFormValues.endDate === formatDateForInput(new Date());
      createOrderBtn.disabled = isSameDate;
      myOrdersBtn.disabled = isSameDate;
    }
    try {
      setView("filtered");
      const id = isChecked ? profileData?._id : "all";
      await getFilteredTransfers({ id, filterFormValues }).unwrap();
    } catch (err) {
      console.log("raw error:", err);
    }
  };
  const filterFormReset = async () => {
    const createOrderBtn = document.getElementById("create-order-btn");
    const myOrders = document.getElementById("myOrders");

    if (createOrderBtn) createOrderBtn.disabled = false;
    if (myOrders) myOrders.disabled = false;
    setFilterFormValues({
      startDate: formatDateForInput(new Date(new Date().setDate(new Date().getDate() - 30))),
      endDate: formatDateForInput(new Date()),
      statuses: [],
    });
    const id = isChecked ? profileData?._id : "all";
    try {
      await getTransfers(id).unwrap();
      setView("unfiltered");
    } catch (err) {
      console.log("reset error:", err);
    }
  };
  /**
   * Determines the CSS class to apply to a table row based on the row's status properties.
   *
   * The function checks several boolean properties on the `row` object in a specific order.
   * The last property that evaluates to `true` determines the returned class name.
   * Possible returned class names include:
   * - 'work-needed-row'
   * - 'currently-working-row'
   * - 'work-done-row'
   * - 'completed-row'
   * - 'bg-success'
   * - 'problem-row'
   * If none of the properties are `true`, the function returns `null`.
   *
   * @param {Object} row - The data object representing a table row, containing status flags.
   * @param {number} rowIndex - The index of the row in the table (unused).
   * @returns {string|null} The CSS class name to apply to the row, or `null` if no status matches.
   */
  const rowClasses = (row, rowIndex) => {
    let classes = null;
    if (row.workNeeded === true) {
      classes = "work-needed-row";
    }
    if (row.currentlyWorking === true) {
      classes = "currently-working-row";
    }
    if (row.workDone === true) {
      classes = "work-done-row";
    }
    if (row.sentFromAgent === true) {
      classes = "completed-row";
    }
    if (row.completed === true) {
      classes = "bg-success";
    }
    if (row.problem === true) {
      classes = "problem-row";
    }
    return classes;
  };

  /**
   * Handles the click event for deleting a transfer.
   * Sets the selected transfer, marks the row to be deleted, and displays the delete confirmation alert.
   *
   * @param {Object} transfer - The transfer object to be deleted.
   */
  const handleDeleteTransferClick = (transfer) => {
    setTransfer(transfer);
    setRowToDelete(transfer._id);
    setShowDeleteAlert(true);
  };
  /**
   * Handles the deletion of a transfer entry.
   *
   * This asynchronous function attempts to delete a transfer by invoking the provided
   * `onDeleteTransfer` callback with the current transfer and filter form values. Upon
   * successful deletion, it resets the transfer state, clears the row selection, and hides
   * the delete confirmation alert. If an error occurs during the process, it logs the error
   * and displays a toast notification to inform the user of the failure.
   *
   * @async
   * @function
   */
  const handleDeleteTransfer = async () => {
    try {
      // const id = isChecked ? profileData?._id : 'all'
      const deleteData = transfer;
      onDeleteTransfer({ dispatch, deleteData });
      setTransfer({ invoiceNo: "" });
      setRowToDelete(null);
      setShowDeleteAlert(false);
    } catch (error) {
      console.error("Failed to delete row:", error);
      showToast("Failed to delete row", "error");
    }
  };
  /**
   * Calculates the DPD size label based on the selected box size from an event input.
   * Updates the state with the corresponding DPD size label.
   *
   * @param {Object} e - The event object from the input element.
   * @param {*} arg - An additional argument (currently unused).
   */
  const calcDPDSize = (e, arg) => {
    const boxSize = parseInt(e.target.value);
    let dpdSize = "";
    switch (boxSize) {
      case 1:
        dpdSize = "XS";
        break;
      case 2:
        dpdSize = "S";
        break;
      case 3:
        dpdSize = "M";
        break;
      case 4:
        dpdSize = "M";
        break;
      case 5:
        dpdSize = "L";
        break;
      case 6:
        dpdSize = "XL";
        break;

      default:
        dpdSize = "";
    }
    setDpdSize(dpdSize);
  };
  let [selectedRows, setSelectedRows] = useState([]);
  let [selectedRowsData, setSelectedRowsData] = useState([]);
  let [selectedWeights, setSelectedWeights] = useState(0);
  let [selectedAgentFees, setSelectedAgentFees] = useState(0);
  let [selectedNoOfToxin, setSelectedNoOfToxin] = useState(0);
  /**
   * Updates the selection state for rows in the main component.
   *
   * Depending on whether a row is being selected or deselected, this function updates:
   * - The list of selected row IDs.
   * - The list of selected row data objects.
   * - The total selected weight.
   * - The total selected agent fees.
   * - The count of selected rows with toxins.
   *
   * @param {Object} row - The row object being selected or deselected.
   * @param {boolean} isSelected - Indicates if the row is being selected (true) or deselected (false).
   */
  const setSelectedRowsMainComp = (type, data, isSelected) => {
    if (type === "single") {
      const row = data;
      if (isSelected) {
        setSelectedRows((prev) => [...prev, row._id]);
        setSelectedRowsData((prev) => [...prev, row]);
        setSelectedWeights((prev) => prev + row.weight);
        setSelectedAgentFees((prev) => prev + row.agentPaymentAmount);
        setSelectedNoOfToxin((prev) => prev + (row.toxins === true ? 1 : 0));
      } else {
        setSelectedRows((prev) => prev.filter((r) => r !== row._id));
        setSelectedRowsData((prev) => prev.filter((r) => r._id !== row._id));
        setSelectedWeights((prev) => prev - row.weight);
        setSelectedAgentFees((prev) => prev - row.agentPaymentAmount);
        setSelectedNoOfToxin((prev) => prev - (row.toxins === true ? 1 : 0));
      }
    } else if (type === "all") {
      const rows = data;
      if (isSelected) {
        setSelectedRows(rows.map((r) => r._id));
        setSelectedRowsData(rows);
        setSelectedWeights(rows.reduce((acc, r) => acc + r.weight, 0));
        setSelectedAgentFees(rows.reduce((acc, r) => acc + r.agentPaymentAmount, 0));
        setSelectedNoOfToxin(rows.reduce((acc, r) => acc + (r.toxins === true ? 1 : 0), 0));
      } else {
        setSelectedRows([]);
        setSelectedRowsData([]);
        setSelectedWeights(0);
        setSelectedAgentFees(0);
        setSelectedNoOfToxin(0);
      }
    }
  };
  /**
   * Handles the submission of a global action on selected transfer rows.
   *
   * Based on the selected action, updates specific fields for each selected row and triggers an update.
   * After updating, resets the selection and related state values.
   *
   * @param {Event} e - The event object from the form submission.
   * @param {Object} values - The values from the form, including the selected action.
   * @param {string} values.action - The action to perform on the selected rows.
   */
  const handleGlobalActionSubmit = (e, values) => {
    const { action } = values;

    const setPaidSentArr = selectedRows.map((id) => {
      let data = { _id: id };
      switch (action) {
        case "set-paid":
          data.agentPaid = true;
          break;
        case "set-unpaid":
          data.agentPaid = false;
          break;
        case "set-not-completed":
          data.completed = false;
          break;
        case "set-work-needed":
          data.workNeeded = true;
          break;
        case "update-status-working":
          data.currentlyWorking = true;
          break;
        case "update-status-completed":
          data.workDone = true;
          break;
        case "set-sent-from-agent":
          data.sentFromAgent = true;
          // data.problem = false;
          break;
        case "set-completed":
          data.completed = true;
          data.problem = false;
          break;
        case "set-problem":
          data.problem = true;
          data.completed = false;
          break;
        case "fully-reset":
          data = {
            _id: id,
            workNeeded: false,
            currentlyWorking: false,
            sentFromAgent: false,
            workDone: false,
            completed: false,
            problem: false,
          };
          break;
        default:
          break;
      }

      return data;
    });
    const id = isChecked ? profileData?._id : "all";
    onUpdateTransfer({ dispatch, updatedData: { updateTransfer: setPaidSentArr, id: id } });
    setSelectedRows([]);
    setSelectedRowsData([]);
    setSelectedWeights(0);
    setSelectedAgentFees(0);
    setSelectedNoOfToxin(0);
  };
  /**
   * Exports selected transfer data to an Excel (.xlsx) file using ExcelJS.
   *
   * This function generates an Excel file containing details of selected transfers,
   * including client information, tracking numbers, weights, payment amounts, and comments.
   * It also appends summary rows for quantity and rewards. The generated file is automatically
   * downloaded in the browser with a filename based on the current date and agent name.
   *
   * @param {Array} transfers_shipping - The array of transfer shipping data to be exported.
   */
  const handleExportExcel = (transfers_shipping) => {
    // const { selectedRowsData, selectedAgentFees, selectedRows } = this.state;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    const data = [[selectedRowsData[0].agent.mainName]];

    for (let i = 0; i < selectedRowsData.length; i++) {
      const row = selectedRowsData[i];

      const trackingArray = Array.isArray(row.trackingNo) ? row.trackingNo : [];
      const newTracking = trackingArray[0] || "";
      const senderType = trackingArray[1] || "";

      const csvRow = [
        i + 1,
        row.clientName ? `${row.clientName} ${row.invoiceNo}` : "",
        row.dpdSize ? `${row.boxSize} (${row.dpdSize})` : "",
        newTracking,
        senderType,
        row.weight + "kg",
        row.agentPaymentAmount + "$",
        row.comments || "",
      ];

      data.push(csvRow);
    }

    data.push([]);
    data.push(["QTY", "Reward", "Reward + Paypal"]);
    data.push([selectedRows.length, `${selectedAgentFees}$`, `${selectedAgentFees + 0.06 * selectedAgentFees}$`]);

    data.forEach((row) => worksheet.addRow(row));

    worksheet.getCell("A1").font = { bold: true, size: 14 };

    worksheet.getColumn("A").width = 25;
    worksheet.getColumn("B").width = 30;
    worksheet.getColumn("D").width = 14;
    worksheet.getColumn("E").width = 30;
    worksheet.getColumn("G").width = 14;
    worksheet.getColumn("H").width = 30;

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${currentDateForTransfer}_${selectedRowsData[0].agent.mainName}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const modalNewOrderContent = (
    <>
      <Row>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              name="clientName"
              id="clientName"
              label="Client's Name"
              type="text"
              errorMessage="Invalid Client's Name"
              autoComplete="off"
              validate={{
                required: {
                  value: true,
                },
              }}
              value={transfer.clientName || ""}
            />
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField
              name="invoiceNo"
              label="Invoice №"
              type="text"
              errorMessage="Invalid Invoice №"
              autoComplete="off"
              value={transfer.invoiceNo || ""}
              validate={{
                required: {
                  value: true,
                },
              }}
            />
          </FormGroup>
        </Col>
        {/* <Col md={3}>
          <FormGroup className='mb-3'>
            <AvField name='trackingNo' label='Tracking' type='text' autoComplete='off' value={transfer.trackingNo || ''} />
          </FormGroup>
        </Col> */}
        <Col md={3}>
          <AvField type="select" name="boxSize" className="form-select" label="Box Size" onChange={calcDPDSize} value={transfer.boxSize ? transfer.boxSize + "/" + boxSizeW : ""}>
            <option>Select Box</option>
            {boxSizes.map((val, key) => (
              <option key={key} value={val.boxSizeNo + "/" + val.volWeight}>
                {val.boxSizeNo}
              </option>
            ))}
          </AvField>
        </Col>
        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField name="weight" label="Weight(KG)" autoComplete="off" errorMessage="Invalid Weight (Please put the number in KG)" type="number" max="50" value={transfer.weight || ""} />
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField name="dpdSize" label="DPD Size" autoComplete="off" type="text" disabled value={dpdSize || ""} />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={2}>
          <FormGroup className="mb-3">
            <AvField name="clientPayment" label="Agent->Client Fees" autoComplete="off" type="number" value={transfer.clientPayment || ""} />
          </FormGroup>
        </Col>
        <Col md={2}>
          <AvField name="customFees" label="Pick up Fees" autoComplete="off" type="number" value={transfer.customFees || ""} />
        </Col>
        <Col md={3}>
          <AvField
            name="senderType"
            label="Sender Type"
            autoComplete="off"
            list="urls"
            value={transfer.senderType || ""}
            errorMessage="Please select sender"
            validate={{
              required: {
                value: true,
              },
            }}
          />
          <datalist id="urls">
            <option value="UPS Standard">(Cheap)</option>
            <option value="UPS Express Saver">(Expensive)</option>
            <option value="CLIENT">(Client pick-up)</option>
          </datalist>
        </Col>
        <Col md={3}>
          <FormGroup className="mb-3">
            <AvField
              type="select"
              name="manager"
              className="form-select"
              label="Manager"
              errorMessage="Please select manager"
              validate={{
                required: {
                  value: true,
                },
              }}
              value={transfer.manager ? transfer.manager._id : profileData._id}
            >
              <option>Choose</option>
              {managers &&
                managers.map((manager) => {
                  return (
                    <option key={manager._id} value={manager._id}>
                      {manager.fname} {manager.sname}
                    </option>
                  );
                })}
            </AvField>
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup className="mt-4 pt-3 font-size-18">
            <AvField name="toxins" type="checkbox" label="Toxins" className="form-check-input" value={transfer.toxins || ""} />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <FormGroup className="mb-3">
            <AvField name="address" label="Address" autoComplete="off" className="font-size-20" type="textarea" rows="4" value={transfer.address || ""} />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <FormGroup className="mb-3">
            <AvField name="comments" label="Comments" autoComplete="off" className="font-size-20" type="textarea" value={transfer.comments || ""} rows="10" />
          </FormGroup>
        </Col>
      </Row>
    </>
  );
  const modalPaymentContent = (
    <Row>
      <Col md={4}>
        <FormGroup className="mb-3">
          <AvField type="select" name="agent" className="form-select" label="Agent" value={transfer.agent ? transfer.agent._id : ""}>
            <option>Select Agent</option>
            {agents.map((agent, key) => (
              <option key={key} value={agent._id}>
                {agent.mainName} - {agent.name}: {agent.address}
              </option>
            ))}
          </AvField>
          {/* <AvField
            name='germanyAddress'
            label='Agent Address'
            autoComplete='off'
            type='text'
            value={
              transfer.germanyAddress || ''
            }
          /> */}
        </FormGroup>
      </Col>
      <Col md={4}>
        <FormGroup className="mb-3">
          <AvField name="comments" label="Comments" autoComplete="off" type="text" value={transfer.comments || ""} />
        </FormGroup>
      </Col>
      <Col md={4}>
        <FormGroup className="mb-3">
          <AvField
            name="agentPaymentAmount"
            label="Payment Amount (USD)"
            type="number"
            autoComplete="off"
            value={transfer.agentPaymentAmount || transfer}
            validate={{
              required: {
                value: true,
              },
            }}
          />
        </FormGroup>
      </Col>
    </Row>
  );
  const globalActionExtraContent = (
    <>
      <div className="d-flex mt-3 ms-5">
        <i className="fas fa-align-justify font-size-22 mt-1 me-2"></i>
        <p className="font-size-22">{selectedRows.length}</p>

        <i className="fas fa-money-bill-alt font-size-22 ms-4 mt-1 me-2"></i>
        <p className="font-size-22">{selectedAgentFees}$</p>

        <i className="fab fa-paypal font-size-22 ms-4 mt-1 me-2"></i>
        <p className="font-size-22">{selectedAgentFees + (6 / 100) * selectedAgentFees}$</p>

        <i className="fas fa-weight font-size-22 ms-4 mt-1 me-2"></i>
        <p className="font-size-22">{selectedWeights.toFixed(2)}Kg</p>

        <i className="fas fa-solid fa-biohazard font-size-22 ms-4 mt-1 me-2"></i>
        <p className="font-size-22">{selectedNoOfToxin}</p>
      </div>
      <Button
        color="success"
        id="exportPDF"
        className="font-16 btn-block btn btn-primary btn-lg me-2"
        onClick={handleExportExcel}
        // disabled={selectedRows.length === 0 ? true : false}
      >
        <i className="mdi mdi-plus-circle-outline me-1" />
        Export data
      </Button>
    </>
  );
  /**
   * Renders custom filter form fields for the logistics page.
   *
   * This component provides three input fields for filtering data:
   * - Start Date: A date picker for selecting the start date.
   * - End Date: A date picker for selecting the end date.
   * - Client Name: A text input for entering the client's name.
   *
   * The input values are controlled by the `filterFormValues` state,
   * and updates are handled via the `setFilterFormValues` function.
   *
   * @constant
   * @type {JSX.Element}
   */
  // const [stDate, setStartDate] = useState(null);
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const filterCustomContent = (
    <>
      <FormGroup className="pe-1">
        <AvField type="date" name="startDate" className="form-control prfilter fw-bold fs-5" value={filterFormValues?.startDate} onChange={handleFilterChange}></AvField>
      </FormGroup>
      <FormGroup className="pe-1">
        <AvField type="date" name="endDate" className="form-control prfilter fw-bold fs-5" value={filterFormValues?.endDate} onChange={handleFilterChange}></AvField>
      </FormGroup>
      <FormGroup className="me-1">
        <button className="btn btn-success save-user">Filter</button>
      </FormGroup>
    </>
  );
  const columns = [
    {
      text: "ID",
      dataField: "_id",
      sort: true,
      editable: false,
      csvExport: false,
      formatter: (cell, row, rowIndex) => (
        <div className="text-center">
          {profileData.permissions.includes("transferAgentTableEdit") ? (
            <>
              {rowIndex + 1}&nbsp;
              {(row.currentlyWorking === true || row.currentlyWorking === true || row.workDone === true) && (
                <Link className={row.workNeeded === true ? "text-secondary" : row.currentlyWorking === true ? "back-needed" : row.workDone === true && "back-working"} to="#">
                  <i
                    className="mdi mdi-arrow-left-circle font-size-18"
                    id="edittooltip"
                    onClick={(e) => handleEditOrderClick(e, row.currentlyWorking === true ? "back-needed" : row.workDone === true && "back-working", row)}
                  ></i>
                </Link>
              )}
              {row.currentlyWorking !== true && row.workDone !== true && (
                <Link className="text-warning" to="#">
                  <i className="mdi mdi-progress-clock font-size-18" id="edittooltip" onClick={(e) => handleEditOrderClick(e, "update-status-working", row)}></i>
                </Link>
              )}
              {row.currentlyWorking === true && (
                <Link className="text-success" to="#">
                  <i className="mdi mdi-progress-check font-size-18" id="edittooltip" onClick={(e) => handleEditOrderClick(e, "update-status-completed", row)}></i>
                </Link>
              )}
            </>
          ) : (
            rowIndex + 1
          )}
        </div>
      ),
    },
    {
      text: "Created",
      dataField: "createdAt",
      sort: true,
      editable: false,
      csvExport: true,
      csvFormatter: (cell, row, rowIndex) => row.createdAt !== null && new Date(row.createdAt).toLocaleDateString("en-GB"),
      formatter: (cellContent, transfer) => <h5 className="font-size-16 mb-1">{new Date(transfer.createdAt).toLocaleDateString("en-GB")}</h5>,
    },
    {
      text: "Client Name",
      dataField: "searchInv",
      sort: true,
      csvExport: true,
      hidden: false,
      editable: false,
      formatter: (cellContent, transfer) => (
        <>
          <h5 className="font-size-16 mb-1 name-width">{transfer.searchInv}</h5>
          <h5 className="font-size-16 mb-1 text-center">
            {transfer.agentPaymentAmount === 0 && (
              <>
                <img src={danger} alt="" width={48} height={48} />
                <p className="make-text-bold mt-1">Please select box size!</p>
              </>
            )}
          </h5>
        </>
      ),
    },
    {
      text: "Tracking",
      dataField: "trackingNo",
      sort: true,
      csvExport: true,
      hidden: false,
      editable: true,
      formatter: (cellContent, transfer) => (
        <div>
          {/* <TrackingColumn tableData={transfer} option='transfer' /> */}
          <TrackingColumnPrTest tableData={transfer} option="transfer" />
          <h5 className="font-size-16 mb-1 text-center">
            {!transfer.trackingNo?.length && (
              <>
                <img src={danger} alt="" width={48} height={48} />
                <p className="make-text-bold mt-1">Please add tracking No!</p>
              </>
            )}
          </h5>
        </div>
      ),
      style: { width: "370px" },
    },
    {
      text: "Agent Address",
      dataField: "agent.mainName",
      sort: true,
      csvExport: true,
      hidden: false,
      editable: false,
      csvFormatter: (cell, row, rowIndex) => `${row.agent.mainName + " " + row.agent.address}`,
      formatter: (cellContent, transfer) => (
        <>
          {transfer.agent && (
            <div className="name-width">
              <p className="make-text-bold mb-0 mt-3">{transfer.agent.mainName}</p>
              <h5 className="font-size-16 mb-1 text-lowercase">{transfer.agent.address}</h5>
            </div>
          )}
          {transfer.searchInv !== null && (
            <>
              <br />
              <div className="form-group">
                <select
                  className="form-control wh-manager-selection"
                  name="mainAgentName"
                  onChange={(e) => handleEditOrderClick(e, "selected", transfer)}
                  // disabled={
                  //   profileData.department !== 'Logistics/Warehouse KR' ||
                  //   (priority_order.completedOrder == true && true)
                  // }
                >
                  <option value="" className="alert-primary">
                    Update
                  </option>
                  {agents.map((agent, index) => {
                    return (
                      <option key={index} value={agent._id + "|" + transfer.agentPaymentAmount}>
                        {" "}
                        {agent.mainName} - {agent.name}: {agent.address}
                      </option>
                    );
                  })}
                </select>
              </div>
            </>
          )}
        </>
      ),
    },
    {
      text: "Box Size",
      // text: (
      //   <div>
      //     Box
      //     <br /> Size
      //   </div>
      // ),
      dataField: "boxSize",
      sort: false,
      csvExport: true,
      hidden: false,
      editable: false,
      csvText: "Box Size",
      csvFormatter: (cell, row, rowIndex) => `${row.boxSize !== 0 && row.boxSize}`,
      formatter: (cellContent, transfer) => (
        <div>
          <h5 className="font-size-16 mb-1 text-center">{transfer.boxSize !== 0 && transfer.boxSize}</h5>
        </div>
      ),
    },
    {
      text: "DPD Size",
      // text: (
      //   <div>
      //     DPD
      //     <br /> Size
      //   </div>
      // ),
      dataField: "dpdSize",
      sort: false,
      csvExport: true,
      hidden: false,
      editable: false,
      csvText: "DPD Size",
      csvFormatter: (cell, row, rowIndex) => `${row.dpdSize}`,
      formatter: (cellContent, transfer) => (
        <div>
          <h5 className="font-size-16 mb-1 text-center">{transfer.dpdSize}</h5>
        </div>
      ),
    },
    {
      text: "Weight",
      dataField: "weight",
      sort: false,
      csvExport: true,
      hidden: false,
      editable: false,
      csvFormatter: (cell, row, rowIndex) => `${row.weight !== null && row.weight + "kg"}`,
      formatter: (cellContent, transfer) => <div>{transfer.weight != null && <h5 className="font-size-16 mb-1 text-center">{transfer.weight}kg</h5>}</div>,
    },
    {
      text: "Address",
      dataField: "address",
      sort: true,
      hidden: false,
      editable: false,
      csvExport: true,
      formatter: (cellContent, transfer) => (
        <div className="text-break name-width">
          <h5>{transfer.address}</h5>
        </div>
      ),
    },
    {
      text: "Sender Type",
      // text: (
      //   <div>
      //     Sender
      //     <br /> Type
      //   </div>
      // ),
      dataField: "senderType",
      sort: false,
      editable: false,
      csvExport: true,
      csvText: "Sender Type",
      formatter: (cellContent, transfer) => <div className="name-width">{transfer.senderType}</div>,
    },
    {
      text: "Manager",
      dataField: "manager.fname",
      sort: true,
      csvExport: true,
      hidden: false,
      editable: false,
      csvFormatter: (cell, row, rowIndex) => `${row.manager.fname + " " + row.manager.sname}`,
      formatter: (cellContent, transfer) => (
        <div>
          <h5 className="font-size-16 mb-1">
            {transfer.manager ? (
              <span>
                <div>
                  {!transfer.manager.profilePic ? (
                    <div className="avatar-sm m-2 d-inline-block">
                      <span className="avatar-title rounded-circle">{transfer.manager?.fname?.charAt(0)}</span>
                    </div>
                  ) : (
                    <img className="rounded-circle avatar-sm m-2" src={transfer.manager.profilePic} alt="profile pic" />
                  )}
                  <span>
                    {transfer.manager.fname} {transfer.manager.sname}
                  </span>
                  <p className="text-muted mb-0">{transfer.manager.workEmail}</p>
                </div>
              </span>
            ) : null}
          </h5>
          {/* <h5 className='font-size-16 mb-1'>
            {transfer.manager ? (
              <>
                <span>
                  {transfer.manager.fname}
                  &nbsp;
                  {transfer.manager.sname}
                </span>
                <p className='text-muted mb-0'>{transfer.manager.workEmail}</p>
              </>
            ) : null}
          </h5> */}
        </div>
      ),
    },
    {
      text: "Comments",
      dataField: "comments",
      sort: true,
      editable: true,
      csvExport: true,
      formatter: (cellContent, transfer) => (
        <div className="comments-width">
          <h5 className="font-size-16 mb-1">{transfer.comments}</h5>
          <h5>{transfer.toxins}</h5>
          {transfer.toxins === true && <i className="fas fa-solid fa-biohazard font-size-20"></i>}
        </div>
      ),
    },
    {
      text: "Agent -> Client Del Fee",
      // text: (
      //   <div>
      //     Agent&#8594;Client Fee
      //     <br /> Delivery Fee
      //   </div>
      // ),
      dataField: "clientPayment",
      editable: true,
      csvExport: true,
      csvText: "Delivery Fee",
      csvType: Number,
      csvFormatter: (cell, row, rowIndex) => (row.clientPayment !== null ? `${"€" + row.clientPayment}` : ""),
      formatter: (cellContent, transfer) => (
        <div className="text-center">
          <NumberFormat value={transfer.clientPayment} displayType="text" thousandSeparator={true} prefix={transfer.agent?.currency} />
        </div>
      ),
    },
    {
      text: "Pick up Fees",
      dataField: "customFees",
      editable: true,
      csvExport: true,
      csvType: Number,
      csvFormatter: (cell, row, rowIndex) => (row.customFees !== null ? `${"₩" + row.customFees}` : ""),
      formatter: (cellContent, transfer) => (
        <div className="text-center">
          <NumberFormat value={transfer.customFees} displayType="text" thousandSeparator={true} prefix="€" />
        </div>
      ),
    },
    {
      text: "Agent Fee",
      dataField: "agentPaymentAmount",
      sort: true,
      editable: false,
      csvExport: true,
      csvFormatter: (cell, row, rowIndex) => (row.agentPaymentAmount !== null ? `${"$" + row.agentPaymentAmount}` : ""),
      formatter: (cellContent, transfer) => (
        <>
          <h5 className="font-size-16 mb-1 text-center">
            {transfer.agentPaymentAmount === 0 ? (
              <>
                <img src={danger} alt="" width={48} height={48} />
                <p className="make-text-bold mt-1">Please select box size!</p>
              </>
            ) : (
              "$" + transfer.agentPaymentAmount
            )}
          </h5>
        </>
      ),
    },
    {
      text: "Agent Paid",
      dataField: "agentPaid",
      sort: true,
      csvExport: true,
      editable: false,
      csvFormatter: (cell, row, rowIndex) => (row.agentPaid === false ? "Not Paid" : "Paid"),
      formatter: (cellContent, transfer) => (
        <>
          <div className={transfer.agentPaid === false ? "btn-warning p-2 rounded-pill text-center" : "btn-success p-2 rounded-pill text-center"}>{transfer.agentPaid ? "Paid" : "Not Paid"}</div>
        </>
      ),
    },
    {
      dataField: "menu",
      text: "Action",
      editable: false,
      csvExport: false,
      headerStyle: {
        minWidth: "150px",
        width: "150px",
      },
      style: {
        minWidth: "150px",
        width: "150px",
        overflow: "visible",
      },
      formatter: (cellContent, transfer) => (
        <div
          className="d-flex align-items-center gap-3"
          style={{
            minWidth: "130px",
            whiteSpace: "nowrap",
            overflow: "visible",
          }}
        >
          <Link className="text-info" to="#">
            <i className="mdi mdi-content-copy font-size-18" id="copytooltip" onClick={() => handleCopyOrderClick(transfer)}></i>
          </Link>

          <Link className="text-success" to="#">
            <i className="mdi mdi-pencil font-size-18" id="edittooltip" onClick={(e) => handleEditOrderClick(e, "not selected", transfer)}></i>
          </Link>

          <ScrollText
            size={20}
            color="#0d6efd"
            style={{
              cursor: "pointer",
              flexShrink: 0,
            }}
            title="View Logs"
            onClick={() => {
              setLogModalOrderId(transfer._id);
              setShowLogModal(true);
            }}
          />

          <Link className="text-danger" to="#">
            <i className="mdi mdi-delete font-size-18" id="deletetooltip" onClick={() => handleDeleteTransferClick(transfer)}></i>
          </Link>
        </div>
      ),
    },
  ];
  const defaultSorted = [
    {
      dataField: "date",
      order: "asc",
    },
  ];
  const selectRow = {
    mode: "checkbox",
    selected: selectedRows,
    onSelectAll: (isSelect, rows, e) => {
      let selectedWeights = 0;
      let selectedAgentFees = 0;
      let selectedNoOfToxin = 0;
      let selectedRows = [];
      let selectedAllArr = [];
      if (!isSelect) {
        setSelectedRows([]);
        setSelectedWeights(0);
        setSelectedAgentFees(0);
        setSelectedNoOfToxin(0);
      } else {
        selectedAllArr = rows;
        setSelectedRowsData(rows);

        selectedAllArr.map((row, i) => {
          selectedRows.push(row._id);
          selectedWeights += row.weight;
          selectedAgentFees += row.agentPaymentAmount;
          selectedNoOfToxin += row.toxins === true ? 1 : 0;
          setSelectedRows(selectedRows);
          setSelectedWeights(selectedWeights);
          setSelectedAgentFees(selectedAgentFees);
          setSelectedNoOfToxin(selectedNoOfToxin);
        });
      }
    },
    onSelect: (row, isSelect, rowIndex, e) => {
      // let selectedRows = [];
      if (!isSelect) {
        selectedRows = selectedRows.filter(function (el) {
          return el !== row._id;
        });
        selectedRowsData = selectedRowsData.filter(function (el) {
          return el !== row;
        });

        selectedWeights -= row.weight;
        selectedAgentFees -= row.agentPaymentAmount;
        selectedNoOfToxin -= row.toxins === true ? 1 : 0;
        setSelectedRows(selectedRows);
        setSelectedRowsData(selectedRowsData);
        setSelectedWeights(selectedWeights);
        setSelectedAgentFees(selectedAgentFees);
        setSelectedNoOfToxin(selectedNoOfToxin);
      } else {
        selectedRowsData.push(row);
        selectedRows.push(row._id);
        selectedWeights += row.weight;
        selectedAgentFees += row.agentPaymentAmount;
        selectedNoOfToxin += row.toxins === true ? 1 : 0;
        setSelectedRows(selectedRows);
        setSelectedWeights(selectedWeights);
        setSelectedAgentFees(selectedAgentFees);
        setSelectedNoOfToxin(selectedNoOfToxin);
      }
    },
  };
  const agentsOptions = [...new Map(groupedAgents.map((v) => [v.mainName, v])).values()]
    .filter((agent) => profileData.accType !== "Outsider" || agent.mainName === `${profileData.fname} ${profileData.sname}`)
    .sort((a, b) => a.mainName.localeCompare(b.mainName)) // ✅ sort alphabetically
    .map((agent) => ({
      key: agent.id,
      icon: <CircleUser size={22} className="ms-3 pe-2" />,
      label: agent.mainName,
    }));

  const statusTypes = [
    {
      key: "pd",
      label: "Paid Orders",
      icon: <Handshake size={22} className="me-2" />,
    },
    {
      key: "unpd",
      label: "Unpaid Orders",
      icon: <Receipt size={22} className="me-2" />,
    },
    {
      key: "cd",
      label: "Completed Orders",
      icon: <Package size={22} className="me-2" />,
    },
    {
      key: "pcd",
      label: "Paid & Completed Orders",
      icon: <CheckCircle size={22} className="me-2" />,
    },

    {
      key: "pr",
      label: "Problem Orders",
      icon: <AlertCircle size={22} className="me-2" />,
    },
  ];
  const actionOptions = [
    { value: "set-paid", text: 'Set to "Agent Paid"', className: "" },
    { value: "set-unpaid", text: 'Set to "Agent Unpaid"', className: "" },
    { value: "set-not-completed", text: 'Set as "Not Completed"', className: "" },
    { value: "set-work-needed", text: 'Set as "Will work on"', className: "work-needed-row" },
    { value: "update-status-working", text: 'Set as "Working on now"', className: "currently-working-row" },
    { value: "update-status-completed", text: 'Set as "Logistics completed"', className: "work-done-row" },
    { value: "set-sent-from-agent", text: 'Set as "Sent from Agent"', className: "completed-row" },
    { value: "set-completed", text: 'Set as "Completed"', className: "bg-success" },
    { value: "set-problem", text: 'Set as "Problem"', className: "problem-row" },
    { value: "fully-reset", text: "Reset / Problem Solved", className: "" },
  ];
  const hasFilterApplied = filters?.agents?.length > 0;
  let data = hasFilterApplied ? filteredStatusesTransfers : filteredTransfers || transfers;
  return (
    <React.Fragment>
      <PageLoader progress={progress} step={step} visible={visible} />
      <div className="page-content">
        <MetaTags>
          <title>Transfers Europe</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="Korupharma" breadcrumbItem="Transfers Europe" />
          <Row className="card-section">
            <Col lg="12">
              <div className="pcss3t logtb pcss3t-effect-scale pcss3t-theme-1">
                <ul>
                  <AnimatePresence key="1" exitBeforeEnter>
                    <motion.li
                      className={`tab-content tab-content-1 typography`}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.3 }}
                      style={{ display: "block" }}
                    >
                      <div id="card-section" ref={cardRef}>
                        <Row className="card-section">
                          <Col lg="12">
                            <Card>
                              <CardBody className="cardBodySticky">
                                <ToolkitProvider keyField="_id" columns={columns} data={data} search>
                                  {(props) => (
                                    <React.Fragment>
                                      <MainContent
                                        page={{
                                          pg: "transfers",
                                          props: props,
                                          cardHeight,
                                          showSearch: true,
                                          showCSV: true,
                                          showInTblFltr: true,
                                          showDbFilter: true,
                                          showSwitch: true,
                                          showBtn: true,
                                          showScndBtn: true,
                                          columns: props.baseProps.columns,
                                          data: props.baseProps.data,
                                          defaultSorted,
                                          selectRow,
                                          rowClasses: rowClasses,
                                          filter: filterFactory(),
                                          cellEdit: cellEditFactory({
                                            mode: "click",
                                            blurToSave: true,
                                            afterSaveCell: (oldVal, newVal, row, column, rowId) => handleSaveCell(oldVal, newVal, row, column, rowId),
                                          }),
                                        }}
                                        myOrdersOptions={{
                                          isChecked: isChecked,
                                          setChecked: setChecked,
                                          showAllOrders: showAllOrders,
                                        }}
                                        // filter options
                                        filterOptions={{ agents: agentsOptions, statuses: statusTypes }}
                                        filterDbOptions={{ filterFormValues, filterCustomContent, filterFormSubmit, setFilterFormValues, filterFormReset }}
                                        setNoDbFilters={setNoDbFilters}
                                        // options for adding/updating modal and button
                                        btnCrtModalOptions={{
                                          btnClass: "create-order-btn",
                                          btnLabelCrt: "Add Transfer Order",
                                          // btnIcon: 'mdi mdi-link-variant-plus me-1',
                                          modalSize: "xl",
                                          modalTitle: isEdit ? "Edit Transfer Order" : "Add Transfer Order",
                                          modalBtnLabel: isEdit ? "Edit Transfer Order" : "Add Transfer Order",
                                          modalAdd: modalOrder,
                                          toggleAddModal: toggleOrderModal,
                                          handleNewClick: handleNewOrderClick,
                                          modalContent: modalNewOrderContent,
                                          handleSubmit: handleNewOrderSubmit,
                                        }}
                                        btnCrtScndModalOptions={{
                                          btnClass: "create-order-btn",
                                          btnLabelCrtScnd: "Add Agent Payment",
                                          btnColorScnd: "info",
                                          // btnIconScnd: 'mdi mdi-cash-multiple me-1',
                                          modalSizeScnd: "lg",
                                          modalTitleScnd: isEdit ? "Edit Payment" : "Add Payment",
                                          modalBtnLabelScnd: isEdit ? "Edit Payment" : "Add Payment",
                                          modalAddScnd: modalPayment,
                                          toggleAddModalScnd: togglePaymentModal,
                                          handleNewClickScnd: handleNewPaymentClick,
                                          modalContentScnd: modalPaymentContent,
                                          handleSubmitScnd: handleNewPaymentSubmit,
                                          errorMsgScnd: error,
                                        }}
                                        // table data opotions
                                        tableOptions={{
                                          includeSelect: true,
                                          manualColumnWidthsIndex: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                                          manualColumnWidths: [120, 230, 390, 220, 50, 50, 90, 220, 150, 300, 270, 150],
                                          // manualColumnWidthsIndex: [3, 4, 5, 6, 7, 8, 10],
                                          // manualColumnWidths: [370, 220, 50, 50, 90, 220, 300]
                                        }}
                                        // table actions
                                        tableActions={{
                                          handleSaveInfo: handleSaveCell,
                                          setSelectedRowsMainComp,
                                          globalActionOptions: actionOptions,
                                          globalActionBtnLbl: "APPLY",
                                          globalActionExtraContent,
                                          selectedRows,
                                          handleGlobalActionSubmit,
                                        }}
                                      />
                                      <SweetAlert
                                        show={showDeleteAlert}
                                        danger
                                        showCancel
                                        confirmBtnText="Yes, delete it!"
                                        confirmBtnBsStyle="danger"
                                        title="Are you sure?"
                                        onConfirm={handleDeleteTransfer}
                                        onCancel={() => setShowDeleteAlert(false)}
                                        focusCancelBtn
                                      >
                                        <p>
                                          The transfer <span className="fw-bold">#{transfer.invoiceNo}</span> will be deleted from the system!
                                        </p>{" "}
                                      </SweetAlert>
                                      <Modal isOpen={showLogModal} toggle={() => setShowLogModal(false)} size="xl" className="log-modal modal-lg-custom">
                                        <ModalHeader toggle={() => setShowLogModal(false)}>
                                          <h3>Transfer Logs</h3>
                                        </ModalHeader>

                                        <ModalBody>
                                          {logsLoading ? (
                                            <p>Loading logs...</p>
                                          ) : logs.length === 0 ? (
                                            <p>No logs found for this transfer.</p>
                                          ) : (
                                            <div className="log-timeline">
                                              {logs.map((log) => (
                                                <div key={log._id} className="timeline-item">
                                                  <div className="timeline-dot" />

                                                  <div className="timeline-content">
                                                    <small className="text-muted">
                                                      {new Date(log.timestamp).toLocaleString("en-GB", {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                        day: "2-digit",
                                                        month: "2-digit",
                                                        year: "numeric",
                                                      })}

                                                      {log.createdBy?.fname && ` • ${log.createdBy.fname} ${log.createdBy.sname}`}
                                                    </small>

                                                    <strong>{log.action}</strong>
                                                    <p className="log-details">{log.details}</p>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </ModalBody>
                                      </Modal>
                                    </React.Fragment>
                                  )}
                                </ToolkitProvider>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    </motion.li>
                  </AnimatePresence>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
